import { useEffect, useState } from "react";
import { SelectSearchMolecule } from ".";
import { usePaginatedCollector } from "app/integration/common/hooks";

/**
 * Komponen dropdown pencarian produk berbasis server-side pagination dengan dukungan fallback selectedProduct.
 * Menjamin opsi produk dan label nama produk selalu tampil pada UI meskipun produk berada di luar batas 50 item halaman awal.
 *
 * @param {Object} props - Properti komponen.
 * @param {string} [props.label] - Label input.
 * @param {string} [props.name] - Nama field formik.
 * @param {string} [props.placeholder] - Placeholder dropdown.
 * @param {number|string} [props.value] - ID produk yang dipilih saat ini.
 * @param {Function} [props.onChange] - Handler saat pilihan produk berubah.
 * @param {Function} [props.onSearch] - Handler saat teks pencarian diketik.
 * @param {boolean} [props.apiSearch=false] - Apakah pencarian memicu query API langsung.
 * @param {string} [props.errorMessage] - Pesan kesalahan validasi.
 * @param {boolean} [props.hideLabel=false] - Menyembunyikan label jika true.
 * @param {boolean} [props.absolute=true] - Posisi menu dropdown absolute.
 * @param {boolean} [props.required=false] - Menandai field wajib diisi.
 * @param {boolean} [props.disabled=false] - Menonaktifkan dropdown.
 * @param {Function} [props.onProductSelected] - Callback saat objek data produk ditemukan/dipilih.
 * @param {string} [props.searchText=""] - Teks pencarian awal.
 * @param {Object} [props.selectedProduct=null] - Objek produk yang telah dipilih dari luar sebagai opsi pasti.
 * @returns {JSX.Element} Elemen dropdown pencarian produk.
 * @sideEffects Mengambil daftar produk terpaginasi dari API backend saat kriteria pencarian berubah.
 */
function ProductSelectSearchMolecule({
  label,
  name,
  placeholder,
  value,
  onChange = () => {},
  onSearch = () => {},
  apiSearch = false,
  errorMessage,
  hideLabel = false,
  absolute = true,
  required = false,
  disabled = false,
  onProductSelected = () => {},
  searchText = "",
  selectedProduct = null,
}) {
  const [localValue, setLocalValue] = useState(value);

  useEffect(() => {
    if (value !== localValue) {
      setLocalValue(value);
    }
  }, [value]);

  const localOnChange = (event) => {
    setLocalValue(event.target.value);
    onChange(event);

    // Call the callback function to pass the selected product to the parent
    const selected =
      products.find((product) => product.id === event.target.value) ||
      (selectedProduct?.id === event.target.value ? selectedProduct : null);
    if (selected && typeof onProductSelected === "function") {
      onProductSelected(selected);
    }
  };

  const localOnSearch = (searchValue) => {
    setProductParams({
      ...productParams,
      product_search: searchValue,
    });
    onSearch(searchValue);
  };

  const [productParams, setProductParams] = useState({
    search: "",
    product_search: searchText,
    order: "code",
    sort: "asc",
    status: null,
    customer_id: null,
    page: 1,
    limit: 50,
  });

  const [
    products,
    setProducts,
    productsLoader,
    collectProducts,
    productPaginationProps,
  ] = usePaginatedCollector({ modelName: "product", params: productParams });

  useEffect(() => {
    collectProducts(productParams);
  }, [productParams]);

  useEffect(() => {
    // Call the callback function to pass the selected product to the parent
    if (!localValue) return;
    const selected = products.find((product) => product.id === localValue);
    if (selected && typeof onProductSelected === "function") {
      onProductSelected(selected);
    }
  }, [products, localValue]);

  // Ekstrak data produk master secara defensif jika selectedProduct berbentuk orderItem (berisi master_product)
  const targetProduct =
    selectedProduct?.master_product ||
    selectedProduct?.product ||
    selectedProduct;

  const targetProductId = targetProduct?.id || selectedProduct?.product_id;
  const targetProductCode =
    targetProduct?.code || selectedProduct?.product_code || "";
  const targetProductName =
    targetProduct?.name || selectedProduct?.product_name || "";

  // Siapkan options dan pastikan selectedProduct selalu ada di daftar opsi agar label nama produk muncul
  const productOptions = products.map((product) => ({
    value: product.id,
    label: (product.code ? product.code + ": " : "") + product.name,
  }));

  if (
    targetProductId &&
    !productOptions.some((opt) => String(opt.value) === String(targetProductId))
  ) {
    productOptions.unshift({
      value: targetProductId,
      label:
        (targetProductCode ? targetProductCode + ": " : "") +
        (targetProductName || "Produk"),
    });
  }

  return (
    <SelectSearchMolecule
      label={label}
      placeholder={placeholder}
      name={name}
      options={productOptions}
      onChange={localOnChange}
      value={localValue}
      absolute={absolute}
      disabled={disabled}
      onSearch={localOnSearch}
      apiSearch={apiSearch}
      errorMessage={errorMessage}
      required={required}
      hideLabel={hideLabel}
    />
  );
}

export default ProductSelectSearchMolecule;
